import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  fetchJson,
  useUser,
  type ClienteResponse,
  type UserResponse,
} from "../services/api";
import { publicApiEndpoint } from "../config";
import { useQuery } from "@tanstack/react-query";

type UserWithStringKind = Omit<UserResponse, "Kind"> & { Kind: string };

interface UserContextType {
  user: UserWithStringKind | undefined;
  clienti: ClienteResponse[] | undefined;
  selectedCliente: ClienteResponse | null;
  setSelectedCliente: (cliente: ClienteResponse) => void;
  isLoading: boolean;
  isAgent: boolean;
  isCustomer: boolean;
  error: Error | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { data: user, isLoading: isLoadingUser, error: errorUser } = useUser();
  const {
    data: clienti,
    isLoading: isLoadingClienti,
    error: errorClienti,
  } = useQuery({
    enabled: !!user && user.Kind === "Agent",
    queryKey: ["clienti"],
    queryFn: async () => {
      return fetchJson<ClienteResponse[]>(
        `${publicApiEndpoint}/agente/clienti`,
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const [selectedCliente, setSelectedCliente] =
    useState<ClienteResponse | null>(null);

  useEffect(() => {
    if (clienti && clienti.length > 0 && !selectedCliente) {
      setSelectedCliente(clienti[0]);
    }
  }, [clienti, selectedCliente]);

  const isAgent = user?.Kind === "Agent";
  const isCustomer = user?.Kind === "Customer";

  const isLoading = isLoadingUser || isLoadingClienti;
  const error = errorUser || errorClienti;

  return (
    <UserContext.Provider
      value={{
        user,
        clienti,
        selectedCliente,
        setSelectedCliente,
        isLoading,
        isAgent,
        isCustomer,
        error: error as Error | null,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
